import React from 'react'
import { BulkInviteAssignableRole } from 'src/app/BulkInvite/bulkInvite.types'
import { OrganizationRoles } from 'src/enums'
import { useTranslate } from 'src/localization/useTranslate'

/**
 * Colocated, localized copy for the Bulk Invite (CSV) surfaces. Every string is
 * a literal `translate('...')` call so the checkLocalization static extractor
 * can verify each key exists in en-US.json. Functions interpolate counts/names.
 */
export function useBulkInviteStrings() {
  const translate = useTranslate()

  return {
    // Entry CTA (People page + Invite flyout)
    ctaLabel: translate('Bulk invite'),
    ctaFromInviteFlyout: translate(
      'Inviting a lot of people? Import from a CSV',
    ),

    // Shell / stepper
    shellTitle: translate('Bulk invite people'),
    shellSubtitle: translate('Import your team from a CSV'),
    stepperAriaLabel: translate('Bulk invite steps'),
    goBackTo: (title: string): string =>
      translate('Go back to ${title}', { title }),

    // Steps
    stepUpload: translate('Upload'),
    stepMapping: translate('Map columns'),
    stepReview: translate('Review & Invite'),

    // Nav
    back: translate('Back'),

    // Upload
    uploadStarterPrompt: translate('Need a starting point?'),
    uploadDropPrompt: translate('or drag and drop'),
    uploadBrowseLink: translate('Click to upload'),
    uploadBrowse: translate('Select a file'),
    uploadInputAriaLabel: translate('Upload a CSV file'),
    // Dropzone hint shown directly under the drop affordance.
    uploadDropzoneHint: translate('CSV only · max 10 MB · up to 500 rows'),
    uploadHint: (mb: number): string =>
      translate('CSV exported as UTF-8. Maximum file size: ${mb} MB.', {
        mb: String(mb),
      }),
    uploadDownloadTemplate: translate('Download template'),
    uploadParsing: translate('Reading your file…'),
    uploadParsingDetail: translate('Detecting your columns and rows.'),
    uploadError: translate('We could not read that file'),
    uploadErrorDetail: translate(
      'Make sure it is a comma-separated CSV saved as UTF-8, then try again.',
    ),
    uploadRetry: translate('Try a different file'),

    // "Preparing your CSV" info card (replaces the HRIS export accordion). The
    // body emphasizes the ".csv" fragment via a rich substitution, so this is a
    // function returning a ReactNode rather than a plain string.
    prepareCardTitle: translate('Preparing your CSV'),
    prepareCardBody: (csv: React.ReactNode): React.ReactNode =>
      translate(
        'Export an employee list from your HRIS as a ${csv} file and upload it here. We’ll match columns in the next step.',
        { csv },
      ),
    prepareCardCsvEmphasis: translate('.csv'),
    prepareFieldsLabel: translate('FIELDS WE LOOK FOR'),
    prepareRequiredPill: translate('Required'),
    prepareOptionalText: translate('Optional'),
    // Fields surfaced on the info card. Email is required; first name, last
    // name, and phone are optional.
    prepareFieldEmail: translate('Email Address'),
    prepareFieldEmailHint: translate('Work email used to send the invite'),
    prepareFieldFirstName: translate('First Name'),
    prepareFieldFirstNameHint: translate('Optional'),
    prepareFieldLastName: translate('Last Name'),
    prepareFieldLastNameHint: translate('Optional'),
    prepareFieldPhone: translate('Phone Number'),
    prepareFieldPhoneHint: translate('Optional'),
    prepareFieldRole: translate('Role'),
    prepareFieldRoleHint: translate('Optional'),
    // Footer stat row: format · max size · max rows.
    prepareStatFormat: translate('CSV'),
    prepareStatFormatLabel: translate('Format'),
    prepareStatSize: translate('10 MB'),
    prepareStatSizeLabel: translate('Max size'),
    prepareStatRows: translate('500'),
    prepareStatRowsLabel: translate('Max rows'),

    // Mapping
    mappingCsvColumnHeader: translate('CSV column'),
    mappingCsvColumnHeaderDescription: translate(
      'Choose a column from your uploaded CSV for this Extend field.',
    ),
    mappingMapsToHeader: translate('Maps to'),
    mappingMapsToHeaderDescription: translate(
      'Extend people fields that the CSV values will import into.',
    ),
    mappingColumnDropdownAriaLabel: (field: string): string =>
      translate('Select the CSV column that maps to ${field}', { field }),
    mappingRequired: translate('Required'),
    mappingOptional: translate('Optional'),
    mappingDontImport: translate('Do not import'),
    mappingMissingRequired: (fields: string): string =>
      translate('Map ${fields} to continue.', { fields }),
    mappingAllRequiredMapped: translate('All required fields are mapped.'),
    mappingSaving: translate('Saving your mapping…'),
    mappingError: translate('We could not save that mapping'),
    mappingErrorDetail: translate('No changes were made. You can try again.'),
    mappingContinueCta: translate('Review invites'),
    mappingWarningsTitle: translate('We adjusted a few things in your file:'),

    // Field labels (kept in sync with EXTEND_FIELDS labelKey)
    fieldFirstName: translate('First name'),
    fieldLastName: translate('Last name'),
    fieldEmail: translate('Email'),
    fieldPhone: translate('Phone'),
    fieldRole: translate('Role'),

    // Review
    reviewLoading: translate('Loading your preview…'),
    reviewInvitedEmpty: translate(
      'No new people to invite. Everyone in your file is already in Extend.',
    ),
    reviewOtherTitle: translate('Other people in your file'),
    reviewOtherSubtitle: translate(
      'Already in Extend or need a fix. Shown for your records. To change who is imported, go back and re-upload.',
    ),
    reviewOtherNotice: translate(
      'These users will not be changed or invited. This table is for your information only.',
    ),
    reviewRoleNoticeOne: translate(
      '1 person will be invited as a Member. You can change their role after they join your org.',
    ),
    reviewRoleNotice: (n: number): string =>
      translate(
        '${n} people will be invited as Members. You can change their role after they join your org.',
        { n: String(n) },
      ),
    reviewSummaryLabel: translate('Bulk invite review summary'),
    reviewSummaryPill: (label: string, count: number): string =>
      translate('${count} ${label}', {
        count: String(count),
        label,
      }),
    reviewSendOne: translate('Send 1 invite'),
    reviewSend: (n: number): string =>
      translate('Send ${n} invites', { n: String(n) }),
    // Singular only for exactly one ready row; plural for zero or many.
    reviewConfirmSend: (count: number): string =>
      count === 1
        ? translate('Confirm & Send Invite')
        : translate('Confirm & Send Invites'),
    reviewSendError: translate('We could not send the invites'),
    reviewSendErrorDetail: translate(
      'No invites were sent. You can try again.',
    ),

    // Columns
    colFullName: translate('Full Name'),
    colEmail: translate('Email Address'),
    colRole: translate('Role in Extend'),
    colPhone: translate('Phone Number'),
    colStatus: translate('Status'),
    // Groups the per-row role dropdown for assistive tech (mirrors the mapping
    // grid's column-dropdown grouping).
    roleDropdownAriaLabel: (name: string): string =>
      translate('Select a role for ${name}', { name }),
    colFailureReason: translate('Reason'),
    roleMemberDefault: translate('Member'),
    roleLabel: (role: BulkInviteAssignableRole): string => {
      switch (role) {
        case OrganizationRoles.member:
          return translate('Member')
        case OrganizationRoles.bookkeeper:
          return translate('Bookkeeper')
        case OrganizationRoles.cardManager:
          return translate('Card Manager')
        case OrganizationRoles.admin:
          return translate('Admin')
      }
    },
    roleBookkeeperFullVisibility: translate('Full account visibility'),
    emptyValue: translate('—'),

    // Preview category labels
    catReady: translate('Ready to Invite'),
    catExistingMember: translate('Already a member'),
    catPendingInvite: translate('Invited'),
    catDuplicate: translate('Duplicate in file'),
    catInvalid: translate('Missing Data'),
    catConflict: translate('Conflict'),
    catClash: translate('Conflicts with a member'),
    catNotInFile: translate('In Extend, not in your file'),

    // Why-this-matters subtext (rendered visibly under the status pill, not in a
    // tooltip) for the informational (read-only) categories. Reassuring copy
    // that explains why a row is skipped and what (if anything) happens to that
    // person.
    catReadyWhy: translate('This row will receive a new invite.'),
    catExistingMemberWhy: translate(
      "Email already matches a member in Extend — we won't change or invite them.",
    ),
    catPendingInviteWhy: translate(
      "A pending invite already exists — we won't send a duplicate.",
    ),
    catDuplicateWhy: translate(
      'This email appears more than once in your file — we invite it only once.',
    ),
    catNotInFileWhy: translate(
      'Already in your org and not changed or removed — just not in this CSV.',
    ),
    catInvalidWhy: translate(
      "This row can't be invited as-is. Fix the email, name, or phone value in your file and re-upload.",
    ),
    catClashWhy: translate(
      "Email conflicts with an existing Extend record — we won't change or invite them.",
    ),

    // Send-row statuses
    sendQueued: translate('Sending…'),
    sendSent: translate('Sent'),
    sendFailed: translate('Failed'),

    // Async "being sent" toast (shown after a successful send, once the modal
    // closes and the admin lands back on the People page).
    sendingToastOne: translate(
      "Sending 1 invite — they'll appear on the People page as they go out.",
    ),
    sendingToast: (count: React.ReactNode): React.ReactNode =>
      translate(
        "Sending ${count} invites — they'll appear on the People page as they go out.",
        { count },
      ),
  }
}
